import { useApplicantAPI, useRequest } from "@certn/api";
import {
  Text,
  ButtonGroup,
  PrimaryButton,
  GhostButton,
  ModalTrigger,
  showToast,
} from "@certn/cascadia";
import {
  Container,
  Divider,
  LinkButton,
  Radio,
  RadioGroup,
} from "@certn/cascadia-react";
import { t } from "i18next";
import React, { useState } from "react";
import { Trans } from "react-i18next";
import { useParams } from "react-router-dom";

import { useDCOReviewContext } from "../DCOReviewContext";
import { DCOReviewPageLayout } from "../DCOReviewLayout";
import { ReviewDecisionLearnMoreModal } from "../ReviewDecisionLearnMoreModal";

export function ReviewDecisionStep() {
  const [disabled, setDisabled] = useState(true);
  const { dispute, setDispute, goToNextStep, goToStep, goToPrevStep } =
    useDCOReviewContext();

  const { caseId } = useParams();

  const { data, isLoading } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/acic-report-certificate` as "/api/internal/applicant/case/{id}/acic-report-certificate/"
  );

  const request = useRequest();

  const handleDCOReviewResponse = async () => {
    const url = `/api/internal/applicant/case/${caseId}/australian-ncchc/dco-review-response/`;
    await request(url, {
      method: "POST",
      body: JSON.stringify({
        dispute,
      }),
    });
  };

  function radioUpdate(disputeSelection: string): void {
    if (disputeSelection === "DISPUTE") {
      setDispute(true);
    } else {
      setDispute(false);
    }
    setDisabled(false);
  }

  function disputeDecisionNextStep(): void {
    if (dispute) {
      goToNextStep();
    } else {
      handleDCOReviewResponse()
        .then(() => {
          goToStep(4);
        })
        .catch((e) => {
          console.error("Error submitting DCO review response:", e);
          showToast(t("Something went wrong, try again"), {
            type: "error",
          });
        });
    }
  }

  return (
    <DCOReviewPageLayout
      onComplete={disputeDecisionNextStep}
      nextDisabled={disabled}
    >
      <Container>
        <Text
          style="global/body-primary"
          color="text-weak"
          testID="review-decision-subtext-1"
        >
          <Trans>
            Please review the results of your Nationally Coordinated Criminal
            History Check below and confirm whether your results are as
            expected.{" "}
            <ModalTrigger
              trigger={() => (
                <LinkButton
                  testID="learn-more-dco-review"
                  textStyle="global/body-primary"
                  textColor="text-weak"
                >
                  <Trans>Learn more</Trans>
                </LinkButton>
              )}
            >
              <ReviewDecisionLearnMoreModal />
            </ModalTrigger>
          </Trans>
        </Text>
        <Text
          style="global/heading-small"
          color="text-weak"
          testID="review-decision-landing-title"
        >
          <Trans>Review your Check Result</Trans>
        </Text>
        <LinkButton
          testID="ncchc-certificate"
          onPress={() => {
            window.open(data?.presigned_url, "_blank");
          }}
          isLoading={isLoading}
          rightIcon="open_in_new"
        >
          {t("NCCHC Certificate")}
        </LinkButton>
        <Divider />
        <Text
          style="global/heading-medium"
          color="text-weak"
          testID="review-decision-section-title"
        >
          <Trans>Are your report results as expected?</Trans>
        </Text>
        <Text
          style="global/body-secondary"
          color="text-weak"
          testID="review-decision-subtext-1"
        >
          <Trans>Select the following based on the review.</Trans>
        </Text>
        <RadioGroup
          isCard
          onChange={(disputeSelection) => {
            radioUpdate(disputeSelection);
          }}
          aria-label="dco-decision-radio-group"
        >
          <Radio
            testID="dco-release-radio"
            value="RELEASE"
            aria-label="dco-release-radio"
          >
            {t("Yes, my results are as expected")}
          </Radio>
          <Radio
            testID="dco-dispute-radio"
            value="DISPUTE"
            aria-label="dco-dispute-radio"
          >
            {t("No, my results are not as expected")}
          </Radio>
        </RadioGroup>
        <ButtonGroup justify="end">
          <GhostButton leftIcon="chevron_left" onPress={goToPrevStep}>
            {t("Go back")}
          </GhostButton>
          <PrimaryButton
            onPress={disputeDecisionNextStep}
            isDisabled={disabled}
            testID="next-step-complete-dco-review-button"
          >
            {t("Next step")}
          </PrimaryButton>
        </ButtonGroup>
      </Container>
    </DCOReviewPageLayout>
  );
}
